import { Fragment, useMemo, useState } from "react"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import SectionCard from "../SectionCard"
import useDebouncedValue from "../../hooks/useDebouncedValue"
import { useCheckApprovalRuleOverlapQuery } from "shared/redux/endpoints/procurementV2"
import AmountCompareChart from "./AmountCompareChart"
import useConditionValueOptions from "./useConditionValueOptions"
import {
  PROBLEM_DESCRIPTIONS,
  PROBLEM_LABELS,
  SELECTION_STATUS,
  amountRangeText,
  amountCompare,
  conflictRows,
  issueMessage,
  overlapCheckPlan,
  overlapReason,
  savedRuleState,
  visibleOverlaps,
  visibleProblems,
} from "./overlapCheck"

const SEVERITY = {
  ERROR: { icon: "error", color: "error" },
  WARNING: { icon: "warning", color: "warning" },
  INFO: { icon: "info", color: "info" },
}
const severityOf = (severity) => SEVERITY[severity] || SEVERITY.INFO

const CheckRow = ({ severity, title, children }) => {
  const { icon, color } = severityOf(severity)
  return (
    <MDBox display="flex" gap={1} bgColor="grey-100" borderRadius="lg" p={1.5} mt={1}>
      <Icon fontSize="small" color={color} sx={{ flexShrink: 0, mt: 0.25 }}>
        {icon}
      </Icon>
      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="button" fontWeight="medium" display="block" sx={{ wordBreak: "break-word" }}>
          {title}
        </MDTypography>
        {children}
      </MDBox>
    </MDBox>
  )
}

const Detail = ({ children }) => (
  <MDTypography variant="caption" color="text" display="block" mt={0.25}>
    {children}
  </MDTypography>
)

const conflictHeading = (rows, overlap) => {
  if (rows.length === 0) return "Both rules have no conditions, so they apply to the same documents."
  const shared = rows.filter((r) => r.shared).map((r) => r.label)
  if (shared.length === 0) {
    return "The rules use different fields, so they conflict on documents that meet both."
  }
  return `${overlap.certainty === "POSSIBLE" ? "May conflict" : "Conflicts"} on: ${shared.join(", ")}`
}

const CellLines = ({ lines }) => (
  <MDBox sx={{ minWidth: 0 }}>
    {(lines.length ? lines : ["Any"]).map((line) => (
      <MDTypography key={line} variant="caption" color="text" display="block" sx={{ wordBreak: "break-word" }}>
        {line}
      </MDTypography>
    ))}
  </MDBox>
)

const ConflictTable = ({ overlap, rows }) => (
  <MDBox mt={0.75}>
    <MDTypography variant="caption" fontWeight="bold" color="warning" display="block">
      {conflictHeading(rows, overlap)}
    </MDTypography>
    {rows.length > 0 && (
      <MDBox
        display="grid"
        columnGap={1.5}
        rowGap={0.5}
        mt={0.5}
        sx={{ gridTemplateColumns: "minmax(4.5rem, auto) minmax(0, 1fr) minmax(0, 1fr)" }}
      >
        <MDTypography variant="caption" fontWeight="bold">
          Field
        </MDTypography>
        <MDTypography variant="caption" fontWeight="bold">
          This rule
        </MDTypography>
        <MDTypography variant="caption" fontWeight="bold">
          {overlap.name}
        </MDTypography>
        {rows.map((r) => (
          <Fragment key={r.field}>
            <MDTypography variant="caption" fontWeight={r.shared ? "bold" : "regular"}>
              {r.label}
            </MDTypography>
            <CellLines lines={r.draft} />
            <CellLines lines={r.other} />
          </Fragment>
        ))}
      </MDBox>
    )}
  </MDBox>
)

const CheckResult = ({ result, conditions, rowIndexes, valueOptionsFor, rowLabel }) => {
  const problems = visibleProblems(result)
  const overlaps = visibleOverlaps(result?.overlaps)
  const issues = result?.issues || []
  const compare = overlaps.length > 0 ? amountCompare(conditions, overlaps) : null

  return (
    <>
      {problems.length === 0 ? (
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small" color="success">
            check_circle
          </Icon>
          <MDTypography variant="button" fontWeight="medium">
            No overlap with other active rules.
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox display="flex" flexDirection="column" rowGap={0.25}>
          {problems.map((p) => (
            <MDTypography key={p.type} variant="caption" color="text">
              <MDTypography
                component="span"
                variant="caption"
                fontWeight="bold"
                color={severityOf(p.severity).color}
              >
                {PROBLEM_LABELS[p.type] || p.type}: {p.count}
              </MDTypography>{" "}
              {PROBLEM_DESCRIPTIONS[p.type]}
            </MDTypography>
          ))}
        </MDBox>
      )}

      {issues.map((issue, idx) => (
        <CheckRow
          key={`${issue.code}-${issue.conditionIndex ?? issue.field}-${idx}`}
          severity={issue.severity}
          title={issueMessage(issue, rowIndexes, conditions, rowLabel)}
        />
      ))}

      {compare && <AmountCompareChart compare={compare} />}

      {overlaps.map((o) => {
        const range = amountRangeText(o.amountOverlap)
        return (
          <CheckRow
            key={o.ruleId}
            severity={o.severity}
            title={`${PROBLEM_LABELS[o.type] || o.type} · ${o.name} (v${o.version})`}
          >
            <Detail>{overlapReason(o, result)}</Detail>
            {range && <Detail>{range}</Detail>}
            {o.type === "CONFLICT" && (
              <ConflictTable overlap={o} rows={conflictRows(conditions, o.conditions, valueOptionsFor, rowLabel)} />
            )}
          </CheckRow>
        )
      })}
    </>
  )
}

const StatusText = ({ label, status }) => {
  const known = SELECTION_STATUS[status]
  return (
    <MDTypography variant="button" fontWeight="regular">
      {label}:{" "}
      <MDTypography component="span" variant="button" fontWeight="bold" color={known?.color || "text"}>
        {known?.label || status}
      </MDTypography>
    </MDTypography>
  )
}

const ConditionCheckPreview = ({ rule, savedRule }) => {
  const valueOptionsFor = useConditionValueOptions()
  const [showSaved, setShowSaved] = useState(false)
  const key = useMemo(() => JSON.stringify(overlapCheckPlan(rule, savedRule)), [rule, savedRule])
  const debouncedKey = useDebouncedValue(key, 500)
  const plan = useMemo(() => JSON.parse(debouncedKey), [debouncedKey])
  const hasWork = JSON.parse(key).args !== null

  const { data, isFetching, isError } = useCheckApprovalRuleOverlapQuery(plan.args, {
    skip: !hasWork || plan.args === null,
  })

  if (!hasWork) return null

  const isStale = key !== debouncedKey || isFetching
  const saved = plan.mode === "new" ? null : savedRuleState(savedRule)
  const current = isError ? null : data?.currentRule
  const edited = isError ? null : data
  const savedMissing = plan.mode !== "new" && data && !isError && !current
  const showAfterEdit = plan.mode !== "saved" && edited

  return (
    <SectionCard
      title="Overlap preview"
      subtitle="How this rule behaves next to the active published rules. Warnings don't block publishing."
      icon="rule"
      action={
        isStale ? (
          <MDTypography variant="caption" color="text">
            Checking…
          </MDTypography>
        ) : null
      }
    >
      {(isError || savedMissing) && !isStale && (
        <MDTypography variant="body2" color="error">
          Couldn&apos;t check this rule right now. You can keep editing.
        </MDTypography>
      )}

      <MDBox sx={{ opacity: isStale ? 0.6 : 1, transition: "opacity 0.15s ease" }}>
        {plan.mode === "new" && edited?.selectionStatus && (
          <MDBox mb={1.5}>
            <StatusText label="Result" status={edited.selectionStatus} />
          </MDBox>
        )}

        {saved && (current || showAfterEdit) && (
          <MDBox mb={1.5}>
            <MDBox display="flex" flexWrap="wrap" alignItems="center" columnGap={1}>
              {current && <StatusText label={saved.title} status={current.selectionStatus} />}
              {current && plan.mode === "edited" && (
                <Icon fontSize="small" color="secondary">
                  arrow_forward
                </Icon>
              )}
              {plan.mode === "edited" && edited && (
                <StatusText label="After this edit" status={edited.selectionStatus} />
              )}
            </MDBox>
            {saved.note && <Detail>{saved.note}</Detail>}
          </MDBox>
        )}

        {plan.mode === "saved" && current && (
          <CheckResult
            result={current}
            conditions={rule.conditions}
            rowIndexes={plan.rowIndexes}
            valueOptionsFor={valueOptionsFor}
          />
        )}

        {showAfterEdit && (
          <CheckResult
            result={edited}
            conditions={rule.conditions}
            rowIndexes={plan.rowIndexes}
            valueOptionsFor={valueOptionsFor}
          />
        )}

        {plan.mode === "edited" && current && (
          <MDBox mt={1.5}>
            <MDButton size="small" variant="text" color="info" onClick={() => setShowSaved((v) => !v)}>
              {showSaved ? "Hide saved rule details" : "Show saved rule details"}
            </MDButton>
            {showSaved && (
              <MDBox mt={0.5}>
                <CheckResult
                  result={current}
                  conditions={savedRule?.conditions}
                  rowIndexes={plan.savedRowIndexes}
                  valueOptionsFor={valueOptionsFor}
                  rowLabel="Saved condition"
                />
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>
    </SectionCard>
  )
}

export default ConditionCheckPreview
